import React from 'react';
import PropTypes from 'prop-types';
import Link from 'gatsby-link';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import { withLastLocation } from 'react-router-last-location';
import { isUserLoggedIn } from 'marketing-website/selectors';

import Button from '@marvelapp/marvel-ui/button';
import Tooltip from '@marvelapp/marvel-ui/tooltip';

import data from 'marketing-website/configs/pages/examples/data';
import features from 'marketing-website/configs/pages/examples/features';

import DiagonalArrow from 'marketing-website/components/modules/Examples/DiagonalArrow';
import Tag from 'marketing-website/components/modules/Tag';
import Page from 'marketing-website/components/modules/Page';
import Image from 'marketing-website/components/modules/Image';
import FullScreenIcon from 'marketing-website/components/icons/FullScreen';

import styles from './style/example.module.css';

function Example({ location, isLoggedIn }) {
  const pageData = data.find(example => example.path === location.pathname);
  const prototypeUrl = `https://marvelapp.com/prototype/${pageData.prototypeSlug}/`;
  const cloneUrl = `/project/clone-bounce/${pageData.prototypeSlug}`;
  const signupAndCopyUrl = `/signup/?next=${cloneUrl}/`;
  let signupText = `Sign up and duplicate`;
  let url = signupAndCopyUrl;
  if (isLoggedIn) {
    url = cloneUrl;
    signupText = `Duplicate to your workspace`;
  }
  const featuresData = pageData.features.map(featureConstant =>
    features.find(feature => feature.id === featureConstant)
  );
  const withoutCurrent = data.filter(
    example => example.path !== location.pathname
  );
  const randomExamples = withoutCurrent
    .sort(() => Math.random() - Math.random())
    .slice(0, 5);

  const playerClassName = classNames(styles.player, {
    [`${styles.playerMobile}`]: pageData.isMobile,
  });

  const meta = {
    title: `${pageData.title} | Marvel`,
    meta: [
      {
        name: 'description',
        content: pageData.description,
      },
    ],
    banner: true,
    header: true,
    footer: true,
  };

  return (
    <Page {...meta}>
      <div className={styles.wrap}>
        <Link className={styles.headingPrefix} to="/examples">
          Example prototype
        </Link>
        <h1 className={styles.heading}>{pageData.name}</h1>
        <p className={styles.subheading}>{pageData.description}</p>
        <div className={styles.tags}>
          {pageData.tags.map(tag => (
            <div className={styles.tag} key={tag}>
              <Tag>{tag}</Tag>
            </div>
          ))}
        </div>
        <div className={playerClassName}>
          <iframe
            className={styles.iframe}
            frameBorder="0"
            allowTransparency="true"
            height="600"
            width="100%"
            src={`${prototypeUrl}?exp=1&iosapp=true`}
          />

          <div className={styles.actions}>
            <a
              className={styles.button}
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid="duplicate-to-workspace"
            >
              <Button
                shape="rounded"
                kind="outlined"
                hover="blue"
                size="medium"
                background="white"
              >
                {signupText}
              </Button>
            </a>
            <Tooltip text="Open in new window" size="medium">
              <a
                href={prototypeUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="open-in-new-window"
              >
                <Button
                  shape="rounded"
                  kind="outlined"
                  hover="green"
                  size="medium"
                  icon={<FullScreenIcon />}
                  background="white"
                />
              </a>
            </Tooltip>
          </div>
        </div>
        <div>
          <h2 className={styles.features}>Features used</h2>
          <div className={styles.grid}>
            {featuresData.map(feature => (
              <div className={styles.feature} key={feature.id}>
                <div
                  className={styles.preview}
                  style={{ color: feature.color }}
                >
                  <div className={styles.previewIcon}>{feature.preview}</div>
                  <div
                    className={styles.featureIcon}
                    style={{ backgroundColor: feature.color }}
                  >
                    {feature.icon}
                  </div>
                </div>
                <h3 className={styles.featureName}>{feature.name}</h3>
                <p className={styles.featureDescription}>
                  {feature.description}
                </p>
                <a
                  href={`https://help.marvelapp.com/hc/en-us/articles/${feature.helpId}`}
                  className={styles.readDocumentation}
                  target="_blank"
                  rel="noreferrer"
                >
                  Read Tutorial{' '}
                  <div className={styles.arrow}>
                    <DiagonalArrow />
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
        <h2 className={styles.morePrototypes}>More prototypes</h2>
        <div className={styles.morePrototypesGrid}>
          {randomExamples.map(feature => (
            <Link to={feature.path} key={feature.id}>
              <div className={styles.previewImage}>
                <Image src={feature.previewImage} hasShadow={false} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Page>
  );
}

Example.propTypes = {
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  history: PropTypes.object.isRequired,
  lastLocation: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    isLoggedIn: isUserLoggedIn(state),
  };
}

export default connect(mapStateToProps)(withLastLocation(withRouter(Example)));



// WEBPACK FOOTER //
// ./src/templates/example.js